import { ReactComponent as InfoIcon } from "assets/icons/InfoIcon.svg";
import { ReactComponent as PencilSVG } from "assets/icons/pencil.svg";
import BottomArrowSvg from "assets/icons/svg/BottomArrowSvg";
import { CheckBox } from "components/CheckBox/CheckBox";
import { InputField } from "components/InputField/InputField";
import { getCurrency, numberFormat } from "helper/numberFormat";
import React, { useCallback, useMemo } from "react";
import Select, { components } from "react-select";

import PaymentInstallment from "components/PaymentInstallment/PaymentInstallment";
import {
	DataFoundWrapper,
	DataNotFoundWrapper,
	NumberOfSelections,
	PaymentAmount,
	PaymentInstallmentAmount,
	PaymentInstallmentCheckbox,
	PaymentInstallmentLeft,
	PaymentInstallmentSelection,
	PaymentInstallmentWrapper,
	SummaryItem,
	SummaryItemCharge,
	SummaryItemInfo,
	SummaryItemLedger,
	SummaryItemLeft,
	SummaryItemSubLedger,
	SummaryTitle,
	TotalDueAmount,
	TotalPayment,
	TotalPaymentEditBtn,
	TotalPaymentEditIcon,
	TotalPaymentField,
	TotalPaymentFieldSign,
	TotalPaymentLeftSide,
	Wrapper,
} from "./Theme";
const regex = /([0-9]*[\.|\,]{0,1}[0-9]{0,2})/s;

const selectControlStyles = {
	color: "#3F4254",
	transition: "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
	paddingTop: "6px",
	paddingBottom: "6px",
	cursor: "pointer",
};
const colourStyles = {
	control: (styles) => ({
		...styles,
		...selectControlStyles,
	}),
	menuList: (styles) => ({
		...styles,
		maxHeight: "150px",
	}),
	menu: (styles) => ({
		...styles,
		zIndex: 9999,
	}),
};

const formatOptionLabel = ({ label, _id }) => {
	return (
		<div className="dropdown-container" key={_id}>
			<div className="icon-name-container d-flex align-items-center justify-content-start">
				<div className="name-container" style={{ marginInlineStart: 10 }}>
					<div>{label}</div>
				</div>
			</div>
		</div>
	);
};

const DropdownIndicator = (props) => {
	return (
		<components.DropdownIndicator {...props}>
			<BottomArrowSvg label="Emoji" />
		</components.DropdownIndicator>
	);
};

const getInstallmentOptions = () => {
	// pattern 2 monthly installments, 3 monthly installments, ... 24 monthly installments
	let installmentOptions = [];
	for (let i = 2; i <= 24; i++) {
		installmentOptions.push({
			value: i,
			label: `${i} monthly installments`,
		});
	}
	return installmentOptions;
};

export const SummaryCard = ({
	chargeSelections,
	formik,
	setEditedTotalPayment,
	editedTotalPayment,
	calculateConvenienceFeeAmount,
	reverseCalculationConvenienceFee,
	calculationTotalPaymentWithCV,
	allowEditTotalPayment,
	calculationPayment,
	setChargeSelections,
	calculateDueAmount,
	dueAmountWithCF,
	isThereInstallmentPayment,
	isInstallmentEnabled,
	setActivePaymentAfter,
	currentPaymentMethod,
}) => {
	const currency = getCurrency();

	const [editTotalPayment, setEditTotalPayment] = React.useState(false);
	const totalDueAmount = useMemo(() => calculateDueAmount(), [calculateDueAmount]);
	const totalPaymentForCV = editedTotalPayment || totalDueAmount;
	const convenienceFeeAmount = useMemo(() => calculateConvenienceFeeAmount(totalPaymentForCV), [
		calculateConvenienceFeeAmount,
		totalPaymentForCV,
	]);

	const onTotalPaymentChange = useCallback(
		(e) => {
			const { value } = e.target;
			const maxValue = value > calculationTotalPaymentWithCV();
			if (value < 1 || maxValue) {
				return;
			}
			formik.setFieldValue("totalPayment", value.match(regex)[0]);
		},
		[formik, calculationTotalPaymentWithCV]
	);

	const onTotalPaymentBlur = useCallback(
		(e) => {
			const totalPayment = formik?.values?.totalPayment;
			
			const minValue = currentPaymentMethod?.convenienceFeeInfo?.type === "$" ? totalPayment <= currentPaymentMethod?.convenienceFeeInfo?.amount : totalPayment < 1;
			if (minValue) {
				formik.setFieldValue("totalPayment", currentPaymentMethod?.convenienceFeeInfo?.amount + 1);
			}
			const totalWithoutConvenience = reverseCalculationConvenienceFee(totalPayment);
			setEditedTotalPayment(totalWithoutConvenience);
			setEditTotalPayment(false);
		},
		[formik, currentPaymentMethod, reverseCalculationConvenienceFee, setEditedTotalPayment]
	);

	const editTotalPaymentHandler = useCallback(() => {
		setEditTotalPayment(true);
	}, []);

	const selectedInstallment = (item) => {
		return item?.installment || null;
	};



	const installmentsOptions = useMemo(() => getInstallmentOptions(), []);

	const calculateInstallmentAmount = (item) => {
		if(!item?.installmentStatus) return "";
		if (item?.installment?.value) {
			return numberFormat({
				number: +item?.amount / item?.installment?.value,
				decimal: 2,
				style: "currency",
			});
		}
		return "";
	};




	return (
		<Wrapper>
			<SummaryTitle>
				<span>Summary</span>
				<InfoIcon />
			</SummaryTitle>
			{chargeSelections?.length === 0 ? (
				<DataNotFoundWrapper>
					<span>No payments were selected yet</span>
				</DataNotFoundWrapper>
			) : (
				<DataFoundWrapper>
					<NumberOfSelections>
						<span>{`You selected ${chargeSelections?.length} payments to pay`}</span>
					</NumberOfSelections>
					{chargeSelections?.map((item) => (
						<SummaryItem>
							<SummaryItemCharge>
								<SummaryItemLeft>
									<SummaryItemInfo>
										<SummaryItemLedger>{item?.Ledger?.name}</SummaryItemLedger>
										<SummaryItemSubLedger>{item?.SubLedger?.name}</SummaryItemSubLedger>
									</SummaryItemInfo>
								</SummaryItemLeft>
								<PaymentAmount>
									{editedTotalPayment ? (
										<del>{`${numberFormat({ number: +item?.amount, decimal: 2, style: "currency" })}`}</del>
									) : (
										<>{`${numberFormat({ number: +item?.amount, decimal: 2, style: "currency" })}`}</>
									)}
								</PaymentAmount>
							</SummaryItemCharge>
							{(isInstallmentEnabled && item?.isInstallmentTransaction) && (
								<PaymentInstallment 
									onAllowInstallmentChange={(e) => {
										calculationPayment();
										const updatedListItem = chargeSelections?.map((val) => {
											if (val.chargeId === item.chargeId) {
												return { 
													...val, 
													installmentStatus: e.target.checked,
													...(!e.target.checked && { installment: null }),
												};
											}
											return val;
										});
										setChargeSelections(updatedListItem);
										setEditedTotalPayment(0);
										setActivePaymentAfter();
									}}
									installmentValue={item?.installmentStatus || false}
									onSelectInstallment={(value) => {
										const updatedListItem = chargeSelections?.map((val) => {
											if (val.chargeId === item.chargeId) {
												return { 
													...val, 
													installment: value,
												};
											}
											return val;
										});
										setChargeSelections(updatedListItem);
									}}
									selectedInstallment={selectedInstallment(item)}
									installmentAmount={calculateInstallmentAmount(item)}
									maxInstallment={item?.installmentDetailsTransaction?.numberOfInstallment}
								/>
							)}

						</SummaryItem>
					))}
					{convenienceFeeAmount > 0 && (
						<SummaryItem>

						<SummaryItemCharge>
							<div>
								<span>Convenience fee of due</span>
							</div>
							<PaymentAmount>
								{numberFormat({ number: +convenienceFeeAmount, decimal: 2, style: "currency" })}
							</PaymentAmount>
						</SummaryItemCharge>
						</SummaryItem>
					)}
					<TotalPayment>
						<p>Payment Total</p>
						{allowEditTotalPayment && editTotalPayment ? (
							<TotalPaymentField>
								<InputField
									placeholder="Payment Total"
									styleWrapper={{ width: "100%" }}
									style={{
										width: "100%",
										paddingLeft: "20px",
										paddingRight: "5px",
										color: "#002E5C",
										fontWeight: "500",
										lineHeight: "13px",
										textAlign: "right",
									}}
									isOptional={false}
									value={formik?.values?.totalPayment}
									name="first_name"
									handleChange={onTotalPaymentChange}
									onBlur={onTotalPaymentBlur}
								/>
								<TotalPaymentFieldSign>{currency}</TotalPaymentFieldSign>
							</TotalPaymentField>
						) : (
							<TotalPaymentLeftSide>
								{(allowEditTotalPayment && !isThereInstallmentPayment()) && (
									<TotalPaymentEditBtn onClick={editTotalPaymentHandler}>
										Edit Amount
										<TotalPaymentEditIcon>
											<PencilSVG
												style={{
													width: 12,
													height: 12,
												}}
											/>
										</TotalPaymentEditIcon>
									</TotalPaymentEditBtn>
								)}

								<span>{numberFormat({ number: +formik?.values?.totalPayment, decimal: 2, style: "currency" })}</span>
							</TotalPaymentLeftSide>
						)}
					</TotalPayment>
					<TotalDueAmount>
						<p>Due Now</p>
						<span>{numberFormat({ number: dueAmountWithCF, decimal: 2, style: "currency" })}</span>
					</TotalDueAmount>
				</DataFoundWrapper>
			)}
		</Wrapper>
	);
};
